import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Group, Select, Text, createStyles} from '@mantine/core';
import React from 'react';

import type {Pagination} from 'lib/Listings';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const PAGE_SIZE_OPTIONS = [
  {label: '10', value: '10'},
  {label: '20', value: '20'},
  {label: '50', value: '50'},
];

interface TablePaginationProps {
  onOffsetChange: (offset: number) => void;
  onPageSizeChange: (size: number) => void;
  pagination: Pagination;
}

export default function TablePagination({
  pagination,
  onOffsetChange,
  onPageSizeChange,
}: TablePaginationProps): JSX.Element {
  const {classes} = useStyles();
  const {offset, limit, total, isExact} = pagination;

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + limit, total);
  const totalLabel = isExact === false ? `${total}+` : `${total}`;

  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  const handlePrev = (): void => {
    onOffsetChange(Math.max(0, offset - limit));
  };

  const handleNext = (): void => {
    onOffsetChange(offset + limit);
  };

  const handlePageSizeChange = (value: string | null): void => {
    if (value != null) {
      onPageSizeChange(Number(value));
    }
  };

  return (
    <Group
      className={classes.container}
      px="sm"
      py="xs"
      {...getTestProps({component: 'TablePagination', element: 'root'})}
    >
      <Text color="dimmed" size="sm">
        Showing {rangeStart}–{rangeEnd} of {totalLabel}
      </Text>

      <Group spacing="sm">
        <Group spacing={4}>
          <Text color="dimmed" size="sm">
            Rows:
          </Text>
          <Select
            aria-label="Rows per page"
            className={classes.pageSizeSelect}
            data={PAGE_SIZE_OPTIONS}
            onChange={handlePageSizeChange}
            size="xs"
            value={String(limit)}
            withinPortal={true}
          />
        </Group>

        <Group spacing={4}>
          <ActionIcon
            aria-label="Previous page"
            disabled={!hasPrev}
            onClick={handlePrev}
            size="sm"
            variant="ghost"
          >
            <BenchlingIcon icon="chevron-left" />
          </ActionIcon>
          <ActionIcon
            aria-label="Next page"
            disabled={!hasNext}
            onClick={handleNext}
            size="sm"
            variant="ghost"
          >
            <BenchlingIcon icon="chevron-right" />
          </ActionIcon>
        </Group>
      </Group>
    </Group>
  );
}

const useStyles = createStyles(() => ({
  container: {
    justifyContent: 'space-between',
  },
  pageSizeSelect: {
    width: 70,
  },
}));
